<template>
    <div v-if="invites.length > 0" class="min-w-50 flex flex-col">
        <HomeSectionHeader><FeatureChip feature="three-sixty">360 Feedback</FeatureChip></HomeSectionHeader>
        <BaseCard theme="muted" padding="lg" class="overflow-hidden text-ink">
            <div class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div class="flex-1">
                    <h3 class="title-body">{{ cardTitle }}</h3>
                    <p class="mt-3 body-small">{{ cardSubtitle }}</p>
                </div>

                <div class="flex w-full flex-col gap-3 md:w-auto md:min-w-65">
                    <BaseLink
                        v-for="invite in invites"
                        :key="invite.invite_token"
                        :href="`/reflections/360/r/${invite.invite_token}/`"
                        :aria-label="inviteSubjectName(invite) ? `Give feedback to ${inviteSubjectName(invite)}` : 'Give feedback'"
                        class="base-btn-neutral group flex items-center justify-between gap-3 text-left"
                        @click="logCtaClick(invite)">
                        <span class="flex items-center gap-1 md:gap-3">
                            <BaseAvatar size="sm" shape="circle"><i class="bi bi-clock" aria-hidden="true" /></BaseAvatar>
                            <span class="details md:body-small"> Give feedback </span>
                        </span>
                        <i class="bi bi-arrow-right transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true"></i>
                    </BaseLink>
                </div>
            </div>
        </BaseCard>
    </div>
</template>

<script setup lang="ts">
import HomeSectionHeader from "~vue/ChatHome/HomeSectionHeader.vue";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import FeatureChip from "~vue/components/FeatureChip.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import type { PendingRaterInvite } from "~vue/types/chat_home";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted } from "vue";

interface Props {
    invites: PendingRaterInvite[];
}

const props = withDefaults(defineProps<Props>(), {
    invites: () => [],
});

const cardTitle = computed(() => {
    if (props.invites.length === 1) {
        return `${props.invites[0].subject_first_name} has invited you to give them feedback`;
    }
    return "You've been invited to give feedback";
});

const cardSubtitle = computed(() => {
    if (props.invites.length === 1) {
        return "Answer a few short questions to help them grow. Your responses are anonymous.";
    }
    return `You have ${props.invites.length} pending 360 feedback requests. Your responses are anonymous.`;
});

onMounted(() => {
    if (props.invites.length > 0) {
        logUserInteraction(AmplitudeEventNames.RATER_ASSESSMENT_CARD_SHOWN, { invite_count: props.invites.length });
    }
});

const inviteSubjectName = (invite: PendingRaterInvite) => invite.subject_full_name || invite.subject_first_name;

const logCtaClick = (invite: PendingRaterInvite) => {
    logUserInteraction(AmplitudeEventNames.RATER_ASSESSMENT_CARD_CTA_CLICKED, {
        subject_first_name: invite.subject_first_name,
        invite_count: props.invites.length,
    });
};
</script>
